'use client';

// eslint-disable-next-line @typescript-eslint/no-deprecated
import { c as _c } from "react/compiler-runtime";
import { Quotes } from '@phosphor-icons/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useRef, useState } from 'react';
import { NPS_DATA, SATISFACTION_DATA, TESTIMONIAL_QUOTES } from '@/src/constants/Research';
function SatisfactionChart() {
  const chartOptions: Highcharts.Options = {
    chart: {
      type: 'bar',
      backgroundColor: 'transparent',
      style: {
        fontFamily: 'inherit'
      },
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
      marginBottom: 0,
      spacing: [0, 0, 0, 0]
    },
    title: {
      text: undefined
    },
    xAxis: {
      categories: SATISFACTION_DATA.map(d => d.label),
      lineWidth: 0,
      tickWidth: 0,
      labels: {
        enabled: false
      }
    },
    yAxis: {
      title: {
        text: undefined
      },
      max: 100,
      gridLineWidth: 0,
      visible: false
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        borderWidth: 0,
        pointPadding: 0.1,
        groupPadding: 0.15
      }
    },
    legend: {
      enabled: false
    },
    credits: {
      enabled: false
    },
    tooltip: {
      outside: true,
      backgroundColor: '#ffffff',
      borderColor: '#EAF1FB',
      borderWidth: 1,
      borderRadius: 10,
      shadow: false,
      padding: 12,
      useHTML: true,
      style: {
        color: '#3D3E40',
        fontSize: '13px'
      },
      formatter() {
        const pt = (this as unknown as {
          point: Highcharts.Point & {
            index: number;
          };
        }).point;
        const entry = SATISFACTION_DATA[pt.index];
        if (!entry) return '';
        return `<div style="min-width:240px"><b>${entry.label}</b><br/><span style="color:#616366">${entry.detail}</span></div>`;
      }
    },
    series: [{
      type: 'bar',
      name: 'Background',
      data: SATISFACTION_DATA.map(() => 100),
      color: '#E6EDF5',
      borderRadius: 6,
      borderWidth: 0,
      grouping: false,
      enableMouseTracking: false,
      showInLegend: false,
      dataLabels: {
        enabled: false
      }
    }, {
      type: 'bar',
      name: 'Positive',
      data: SATISFACTION_DATA.map(d_0 => d_0.positive),
      color: '#2FA67A',
      dataLabels: {
        enabled: true,
        format: '{y}%',
        align: 'right',
        inside: true,
        style: {
          fontSize: '13px',
          fontWeight: '600',
          color: '#ffffff',
          textOutline: 'none'
        }
      }
    }]
  };
  return <div>
      <h3 className="mb-4 text-base font-semibold text-[#3D3E40]">
        Educator Satisfaction
      </h3>
      {/* Labels rendered as real HTML on the left, chart fills the right */}
      <div className="flex items-stretch gap-0">
        <div className="flex shrink-0 flex-col justify-around pr-3" style={{
        width: 160
      }}>
          {SATISFACTION_DATA.map(d_1 => <p key={d_1.label} className="text-right text-[13px] leading-snug text-[#616366]" style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
              {d_1.label}
            </p>)}
        </div>
        <div className="min-w-0 flex-1">
          <HighchartsReact highcharts={Highcharts} options={chartOptions} containerProps={{
          style: {
            width: '100%'
          }
        }} />
        </div>
      </div>
      <p className="mt-1 text-xs italic text-[#616366]">
        % Agree or Strongly Agree. Full data in research report.
        <sup>1</sup>
      </p>
    </div>;
}
function NPSChart() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(300);
  const [isLarge, setIsLarge] = useState(false);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) => {
      if (entry) setContainerWidth(Math.round(entry.contentRect.width));
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    setIsLarge(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsLarge(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const diameter = Math.min(containerWidth - 32, isLarge ? 260 : 320);
  const chartHeight = isLarge ? diameter + 16 : Math.round(diameter / 2) + 28;
  const options: Highcharts.Options = {
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
      style: {
        fontFamily: 'inherit'
      },
      height: chartHeight,
      margin: [0, 0, 0, 0],
      spacing: [0, 0, 0, 0]
    },
    title: {
      text: undefined
    },
    plotOptions: {
      pie: {
        startAngle: isLarge ? 0 : -90,
        endAngle: isLarge ? 360 : 90,
        center: isLarge ? ['50%', '50%'] : ['50%', '100%'],
        size: diameter,
        innerSize: '62%',
        borderWidth: 4,
        borderColor: '#ffffff',
        borderRadius: 8,
        dataLabels: {
          enabled: false
        },
        states: {
          hover: {
            halo: {
              size: 4
            }
          }
        }
      }
    },
    tooltip: {
      outside: true,
      backgroundColor: '#ffffff',
      borderColor: '#EAF1FB',
      borderWidth: 1,
      borderRadius: 10,
      shadow: false,
      padding: 12,
      style: {
        color: '#3D3E40',
        fontSize: '13px'
      },
      formatter() {
        const pt = (this as unknown as {
          point: Highcharts.Point;
        }).point;
        return `<b>${pt.name}</b>: ${pt.y}%`;
      }
    },
    legend: {
      enabled: false
    },
    credits: {
      enabled: false
    },
    series: [{
      type: 'pie',
      data: [{
        name: 'Promoters',
        y: 81.3,
        color: '#2FA67A'
      }, {
        name: 'Passives',
        y: 12.5,
        color: '#E9B949'
      }, {
        name: 'Detractors',
        y: 6.3,
        color: '#9AA6B2'
      }]
    }]
  };
  return <div className="flex h-full flex-col">
      <h3 className="mb-4 text-base font-semibold text-[#3D3E40]">
        Net Promoter Score
      </h3>

      <div className="flex flex-1 flex-col justify-center">
        <div className="relative" ref={wrapperRef}>
          <HighchartsReact highcharts={Highcharts} options={options} containerProps={{
          style: {
            width: '100%'
          }
        }} />
          <div className={`absolute left-1/2 -translate-x-1/2 text-center ${isLarge ? 'top-1/2 -translate-y-1/2' : 'bottom-0 pb-2'}`}>
            <div className="text-4xl font-bold text-[#3D3E40]">
              +{NPS_DATA.value}
            </div>
            <div className="text-sm font-semibold text-[#527B94]">
              Excellent
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          {[{
          label: 'Promoters',
          pct: '81.3%',
          color: '#2FA67A'
        }, {
          label: 'Passives',
          pct: '12.5%',
          color: '#E9B949'
        }, {
          label: 'Detractors',
          pct: '6.3%',
          color: '#9AA6B2'
        }].map(item => <div key={item.label} className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{
            backgroundColor: item.color
          }} />
              <span className="text-sm text-[#616366]">{item.label}</span>
              <span className="ml-auto text-sm font-semibold text-[#3D3E40]">
                {item.pct}
              </span>
            </div>)}
        </div>
      </div>
    </div>;
}
function Testimonials() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="mt-16"><div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{TESTIMONIAL_QUOTES.map(_temp)}</div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(quote, index) {
  return <div key={index} className="flex flex-col rounded-2xl border border-[#EAF1FB] bg-white p-6"><Quotes weight="duotone" size={28} color="#318AE5" className="mb-4 shrink-0" /><p className="flex-1 text-[15px] leading-relaxed text-[#3D3E40]">{quote.text}</p><div className="mt-5 border-t border-[#EAF1FB] pt-4"><span className="rounded-full bg-[#f2faff] px-3 py-1.5 text-xs font-medium text-[#527B94]">{quote.role}</span></div></div>;
}
export const StudyResults = () => {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      background: "linear-gradient(to bottom, #F7FCFF 0%, #F7FCFF 65%, #ffffff 100%)"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mb-3 flex justify-center"><span className="rounded-2xl bg-white px-4 py-2 text-[13px] font-medium text-[#527B94]">Study Results</span></div>;
    t2 = <h2 className="text-center text-2xl font-medium text-[#3D3E40] md:text-3xl">What Educators Say</h2>;
    t3 = <p className="mx-auto mt-3 max-w-2xl text-center text-[15px] text-[#616366]">We surveyed special education professionals across urban, suburban, and rural schools. Here's what they told us.</p>;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="rounded-2xl bg-white p-6 lg:col-span-2 md:p-8"><SatisfactionChart /></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <section className="py-16 md:py-24" style={t0}><div className="container mx-auto px-6 md:px-10 2xl:px-[10rem]">{t1}{t2}{t3}<div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-3">{t4}<div className="flex flex-col rounded-2xl bg-white p-6 md:p-8"><NPSChart /></div></div><Testimonials /><p className="mx-auto mt-10 max-w-3xl text-center text-xs text-[#9AA6B2]">This page presents Ablespace's research basis, which earned Digital Promise's Research-Based Design (ESSA Tier 4) Product Certification. Study conducted 2026 with special education professionals across multiple school settings. All participant quotes are anonymous and used with consent.</p></div></section>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};